/*
 * Copyright (c) Nexign, JSC, 2019-2022.
 */
/**
 * Widget Order Inquiries Component
 *
 * @author Yakov Gushchin
 * @author Fedorov Maxim
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @version 1.20.0
 * @since 1.0.1
 */

import { Fragment, memo, useEffect } from 'react';

import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { AuthorityRoleEnum } from '@pages/PageAuth/constants/authConstants';
import { useCheckCustomRole } from '@utils/useCheckCustomRole';

import { Skeleton } from '../../../../../../components/common';
import { FlexColumnStyled } from '../../../../../../components/common/Block/Flexbox';
import useTableFooterButtons from '../../../../../../components/common/TableFooter/hooks/useTableFooterButtons';
import Tab from '../../../../../../components/common/Tabs/Tab';
import Tabs from '../../../../../../components/common/Tabs/Tabs';
import { TableFooterNameType } from '../../../../../../components/Table/types/tableTypes';
import { pushGTMAnalytic } from '../../../../../../modules/analytics/GoogleTagManager/GTMActions';
import GTMOperations from '../../../../../../modules/analytics/GoogleTagManager/GTMOperations';
import { EGTMWidgetOrderInquiriesTab } from '../../../../../../modules/analytics/GoogleTagManager/GTMOperations/GTMWidgetOrderInquiriesOperations';
import { IGTMPushAnalyticPayload } from '../../../../../../modules/analytics/GoogleTagManager/types/GTMActionTypes';
import initTranslationCommon from '../../../../../../modules/internationalization/constants/common';
import initTranslationPageDashboard
    from '../../../../../../modules/internationalization/constants/pages/dashboard/dashboard';
import ICommonTranslation from '../../../../../../modules/internationalization/types/common';
import IPageDashboardTranslation from '../../../../../../modules/internationalization/types/pages/dashboard/dashboard';
import { makeSelectLocalStorageSelectedAccount } from '../../../../../../modules/storage/storageSelectors';
import { IRootReducer } from '../../../../../../reducers/rootReducer';
import { makeSelectAuthoritiesIsAssistant } from '../../../../../PageAuth/selectors/authSelectors';
import { WidgetSkeletonParagraph } from '../../constants/widgetSkeletonContants';
import { WidgetTitleStyled } from '../../styled/WidgetStyled';
import { IWidgetContainerProps } from '../../WidgetContainer';
import WidgetError from '../../WidgetError';
import { IWidgetSearchAccountsItem } from '../../WidgetSearchAccounts/types/widgetSearchAccountsTypes';
import { EWidgetStatus } from '../../widgetTypes';
import { WidgetOrderInquiriesStyled } from '../styled/widgetOrderInquiriesStyled';
import { widgetInquiriesRequest, widgetOrderRequest } from '../widgetOrderInquiriesAction';
import {
    makeSelectorInquiriesGetInquiries,
    makeSelectorInquiriesIsLoading,
    makeSelectorInquiriesStatus,
    makeSelectorOrderIsLoading,
    makeSelectorOrdersGetOrders,
    makeSelectorOrderStatus,
} from '../widgetOrderInquiriesSelector';
import { IWidgetInquiries, IWidgetOrders } from '../widgetOrderInquiriesTypes';
import WidgetInquiriesEmpty from './WidgetInquiriesEmpty';
import WidgetOrderInquiriesSuccessful from './WidgetOrderInquiriesSuccessful';
import WidgetOrdersEmpty from './WidgetOrdersEmpty';

export enum EWidgetOrderInquiries {
    INQUIRIES = 'inquiries',
    ORDER = 'order',
}

interface IWidgetOrderInquiriesProps {
    translation: IPageDashboardTranslation & ICommonTranslation;
}

export type TWidgetOrderInquiries = IWidgetOrderInquiriesProps & IConnectedProps & IDispatchProps;

export const WidgetOrderInquiries = (props: TWidgetOrderInquiries) => {
    const {
        inquiries,
        loadingInquiries,
        loadingOrder,
        loadInquiries,
        loadOrders,
        orders,
        statusInquiries,
        statusOrder,
        translation,
    } = props;

    const { inquiryCreate } = useTableFooterButtons(
        TableFooterNameType.accountId,
        undefined,
        props.selectedAccount.id.toString(),
    );

    const isCustomRoleInquiryCreate = useCheckCustomRole(false, AuthorityRoleEnum.ROLE_INQUIRIES_MANAGE);

    useEffect(() => {
        loadOrders();
        loadInquiries();
    }, [loadInquiries, loadOrders, props.selectedAccount.id]);

    const onOrderTabClick = (): void => {
        props.pushGtmAnalytic({
            operation: GTMOperations.widgetOrderInquiries
                .clickTabWidgetOrderInquiries(EGTMWidgetOrderInquiriesTab.orders),
            withAccountType: true,
            withSusrId: true,
            withUserRole: true,
        });
    };

    const onInquiryTabClick = (): void => {
        props.pushGtmAnalytic({
            operation: GTMOperations.widgetOrderInquiries
                .clickTabWidgetOrderInquiries(EGTMWidgetOrderInquiriesTab.inquiries),
            withAccountType: true,
            withSusrId: true,
            withUserRole: true,
        });
    };

    const openCreateInquiry = () => {
        inquiryCreate.onClick();
    };

    return (
        <FlexColumnStyled>
            <WidgetOrderInquiriesStyled
                data-widget="WidgetOrderInquiries"
            >
                <WidgetTitleStyled>
                    {translation.widgets.orderInquiries.title}
                </WidgetTitleStyled>

                <Tabs>
                    <Tab
                        className="widget-order-inquiries__tab-order"
                        label={translation.widgets.orderInquiries.order.orderLabel}
                        onClick={onOrderTabClick}
                    >
                        <Skeleton
                            count={3}
                            isLoading={loadingOrder}
                            render={() => WidgetSkeletonParagraph}>
                            <Fragment>
                                {{
                                    empty: <WidgetOrdersEmpty
                                        helpText={translation.widgets.orderInquiries.order.empty.helpText}
                                        title={translation.widgets.orderInquiries.order.empty.title}
                                    />,
                                    error: <WidgetError
                                        text={translation.widgets.orderInquiries.fail.text}
                                    />,
                                    success: <WidgetOrderInquiriesSuccessful
                                        className="widget-order-inquiries__success-orders"
                                        items={orders}
                                        pushGtmAnalytic={props.pushGtmAnalytic}
                                        translation={translation}
                                        type={EWidgetOrderInquiries.ORDER}
                                    />,
                                }[statusOrder]}
                            </Fragment>
                        </Skeleton>
                    </Tab>
                    <Tab
                        className="widget-order-inquiries__tab-inquiries"
                        label={translation.widgets.orderInquiries.inquiries.inquiriesLabel}
                        onClick={onInquiryTabClick}
                    >
                        <Skeleton
                            count={3}
                            isLoading={loadingInquiries}
                            render={() => WidgetSkeletonParagraph}>
                            <Fragment>
                                {{
                                    empty: <WidgetInquiriesEmpty
                                        isAllowToCreateInquiry={(!props.isAssistant || isCustomRoleInquiryCreate)}
                                        openCreateInquiry={openCreateInquiry}
                                        translation={translation}
                                    />,
                                    error: <WidgetError
                                        text={translation.widgets.orderInquiries.fail.text}
                                    />,
                                    success: <WidgetOrderInquiriesSuccessful
                                        className="widget-order-inquiries__success-inquiries"
                                        isAllowToCreateInquiry={!props.isAssistant || isCustomRoleInquiryCreate}
                                        items={inquiries}
                                        openCreateInquiry={openCreateInquiry}
                                        pushGtmAnalytic={props.pushGtmAnalytic}
                                        translation={translation}
                                        type={EWidgetOrderInquiries.INQUIRIES}
                                    />,
                                }[statusInquiries]}
                            </Fragment>
                        </Skeleton>
                    </Tab>
                </Tabs>
            </WidgetOrderInquiriesStyled>
        </FlexColumnStyled>
    );
};

interface IConnectedProps {
    isAssistant: boolean;
    inquiries: IWidgetInquiries[];
    loadingInquiries: boolean;
    loadingOrder: boolean;
    orders: IWidgetOrders[];
    selectedAccount: IWidgetSearchAccountsItem;
    statusInquiries: EWidgetStatus;
    statusOrder: EWidgetStatus;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    isAssistant: makeSelectAuthoritiesIsAssistant(),
    inquiries: makeSelectorInquiriesGetInquiries(),
    loadingInquiries: makeSelectorInquiriesIsLoading(),
    loadingOrder: makeSelectorOrderIsLoading(),
    orders: makeSelectorOrdersGetOrders(),
    selectedAccount: makeSelectLocalStorageSelectedAccount(),
    statusInquiries: makeSelectorInquiriesStatus(),
    statusOrder: makeSelectorOrderStatus(),
});

interface IDispatchProps {
    loadInquiries: () => void;
    loadOrders: () => void;
    pushGtmAnalytic: (payload: IGTMPushAnalyticPayload) => void;
}

const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    loadInquiries: () => dispatch(widgetInquiriesRequest()),
    loadOrders: () => dispatch(widgetOrderRequest()),
    pushGtmAnalytic: (payload: IGTMPushAnalyticPayload) => dispatch(pushGTMAnalytic(payload)),
});

const defaultProps: Partial<IWidgetContainerProps> = {
    translation: { ...initTranslationPageDashboard, ...initTranslationCommon },
};

WidgetOrderInquiries.defaultProps = defaultProps;

export default connect(mapStateToProps, mapDispatchToProps)(memo(WidgetOrderInquiries));
